import { useTranslation } from 'react-i18next';
import { FUNDAMENTALS, BEHAVIORS } from '../../data';
import { EditableSpan } from '../EditableSpan';
import { useBuilderContext } from '../../context/BuilderContext';

interface PreviewQuestionHeaderProps {
  qIndex: number;
  isSpanish: boolean;
  activeLang: string;
  getQText: (i: number) => string;
  hasMissingTranslation: (i: number) => boolean;
  saveTranslation: (i: number, val: string) => void;
  onOpenIconPicker: (i: number) => void;
  collapsedQs: Set<number>;
  toggleQCollapse: (i: number) => void;
}

export function PreviewQuestionHeader({
  qIndex,
  isSpanish,
  activeLang,
  getQText,
  hasMissingTranslation,
  saveTranslation,
  onOpenIconPicker,
  collapsedQs,
  toggleQCollapse,
}: PreviewQuestionHeaderProps) {
  const { t } = useTranslation();
  const { form, setForm } = useBuilderContext();

  const q = form.questions[qIndex];
  if (!q) return null;

  const fObj = q.fundamental ? FUNDAMENTALS.find(f => f.id === q.fundamental) : null;
  const bObj = (q.fundamental && q.coreBehavior)
    ? (BEHAVIORS[q.fundamental] || []).find(b => b.id === q.coreBehavior)
    : null;
  const missing = hasMissingTranslation(qIndex);
  const translatedText = getQText(qIndex);
  const isCollapsed = collapsedQs.has(qIndex);

  return (
    <div className="pvx-block-header-wrap">
      <div className="pvx-block-collapse-bar" onClick={() => toggleQCollapse(qIndex)}>
        <span className="pvx-block-num">P{qIndex + 1}</span>
        <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'} pvx-collapse-chevron`} />
      </div>
      <div className="pvx-block-header">
        <div
          className="pvx-icon-clickable"
          onClick={() => onOpenIconPicker(qIndex)}
          title={t('admin.builderPreviewChangeIcon')}
        >
          {q.icon
            ? <img src={q.icon} alt="" className="pvx-q-icon" style={{ width: 36, height: 36 }} />
            : <span style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'rgba(52,47,130,0.08)', color: '#342F82', fontSize: '1.1rem' }}>
                <i className="fas fa-image" />
              </span>
          }
          <span className="pvx-icon-edit-badge"><i className="fas fa-pen" /></span>
        </div>
        <div className="pvx-block-meta">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {fObj && <span className="pvx-fund-tag">{fObj.short}</span>}
            {bObj && <span className="pvx-habit-tag">{bObj.name}</span>}
            <span className={`pvx-enfoque-tag ${q.enfoque}`}>
              {q.enfoque === 'individual'
                ? <><i className="fas fa-user-check" /> {t('admin.builderPreviewIndividual')}</>
                : <><i className="fas fa-users" /> {t('admin.builderApproachCollective')}</>
              }
            </span>
            <span className={`pvx-mode-badge ${q.presentMode === 'individual' ? 'individual' : 'matrix'}`}>
              {q.presentMode === 'individual'
                ? <><i className="fas fa-user" /> {t('admin.builderPreviewModeIndividual')}</>
                : <><i className="fas fa-table" /> {t('admin.builderPreviewModeMatrix')}</>
              }
            </span>
          </div>
          <div className="pvx-block-question-wrap">
            {isSpanish ? (
              <EditableSpan
                value={q.text}
                onChange={v => setForm(f => ({
                  ...f,
                  questions: f.questions.map((qq, i) => i === qIndex ? { ...qq, text: v } : qq),
                }))}
                className="pvx-editable-question"
              />
            ) : (
              <EditableSpan
                key={`${activeLang}-${qIndex}`}
                value={translatedText}
                onChange={v => saveTranslation(qIndex, v)}
                className="pvx-editable-question"
              />
            )}
            {missing && <em className="pvx-missing-note">{t('admin.builderPreviewMissingTranslation')}</em>}
          </div>
        </div>
      </div>
    </div>
  );
}
